import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmationService, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { Image } from 'primeng/image';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import Language from '../../../../../types/language/Language';
import MarketAvailability from '../../../../../types/market-availability/MarketAvailability';
import SampleLanguage from '../../../../../types/sample-language/SampleLanguage';
import SampleProcessLog from '../../../../../types/sample-process-log/SampleProcessLog';
import SampleBranding from '../../../../../types/sample-vehicle-model/SampleBranding';
import Sample from '../../../../../types/sample/Sample';
import { SampleState } from '../../../../../types/sample/SampleState';
import SampleWriteDTO from '../../../../../types/sample/SampleWriteDTO';
import { Role } from '../../../../../types/shared/Role';
import VehicleModel from '../../../../../types/vehicle-model/VehicleModel';
import { CanToggleOnHoldDirective } from '../../../directives/can-toggle-on-hold.directive';
import { IsMaintainerDirective } from '../../../directives/is-maintainer.directive';
import { IsProcessOwnerDirective } from '../../../directives/is-process-owner.directive';
import { IsProducerDirective } from '../../../directives/is-producer.directive';
import { EnumLabelPipe } from '../../../pipes/enum-label.pipe';
import { FormatDateTime } from '../../../pipes/format-date-time';
import { FormatDatePipe } from '../../../pipes/format-date.pipe';
import { RoleNamePipe } from '../../../pipes/role-name.pipe';
import { SampleBrandingPipe } from '../../../pipes/sample-branding.pipe';
import { FieldOfUseSeverityPipe } from '../../../pipes/sample-field-of-use-severity.pipe';
import { SampleFormatPipe } from '../../../pipes/sample-format.pipe';
import { SampleStateSeverityPipe } from '../../../pipes/sample-state-severity.pipe';
import { AuthService } from '../../../services/auth.service';
import { SampleHelperService } from '../../../services/helper/sample-helper.service';
import { LanguageService } from '../../../services/language.service';
import { MarketAvailabilityService } from '../../../services/market.availability.service';
import { SampleProcessLogService } from '../../../services/sample-process-log.service';
import { SampleService } from '../../../services/sample.service';
import { VehicleModelService } from '../../../services/vehicle-model.service';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import {
    AddVehicleModelsDialogComponent
} from '../../shared/samples/add-vehicle-models-dalog/add-vehicle-models-dialog.component';
import {
    ChangeSampleStateDialogComponent
} from '../../shared/samples/change-sample-state-dialog/change-sample-state-dialog.component';
import {
    ChangeSampleVehicleModelAvailabilityDialogComponent
} from '../../shared/samples/change-sample-vehicle-model-availability-dialog/change-sample-vehicle-model-availability-dialog.component';
import {
    ToggleOnHoldDialogComponent
} from '../../shared/samples/toggle-on-hold-dialog/toggle-on-hold-dialog.component';
import { UpdateSampleDialogComponent } from '../../shared/samples/update-sample-dialog/update-sample-dialog.component';
import {
    ChangeSalesCodesDialogComponent
} from '../../shared/vehicle-models/change-sales-codes-dialog/change-sales-codes-dialog.component';

@Component({
    selector: 'app-sample-detail',
    templateUrl: './sample-detail.component.html',
    styleUrls: [ './sample-detail.component.scss' ],
    imports: [
        Button,
        RouterLink,
        PageHeaderComponent,
        UpdateSampleDialogComponent,
        TableModule,
        SharedModule,
        Tag,
        FieldOfUseSeverityPipe,
        SampleStateSeverityPipe,
        EnumLabelPipe,
        FormatDateTime,
        ChangeSampleStateDialogComponent,
        FormatDatePipe,
        AddVehicleModelsDialogComponent,
        ChangeSampleVehicleModelAvailabilityDialogComponent,
        IsMaintainerDirective,
        IsProducerDirective,
        IsProcessOwnerDirective,
        CanToggleOnHoldDirective,
        ChangeSalesCodesDialogComponent,
        FormsModule,
        Image,
        Tooltip,
        RoleNamePipe,
        SampleFormatPipe,
        IconField,
        InputIcon,
        InputText,
        ToggleOnHoldDialogComponent,
        SampleBrandingPipe,
        Select
    ]
})
export class SampleDetailComponent implements OnInit {

    public sample: Sample | null = null;
    public vehicleModels: VehicleModel[] = [];
    public editableSample: SampleWriteDTO = new SampleWriteDTO({});
    public processLogs: SampleProcessLog[] = [];
    public languages: Language[] = [];
    public selectedVehicleModel: VehicleModel | null = null;
    public marketAvailabilities: MarketAvailability[] = [];

    public displayUpdateDialog = false;
    public displayChangeStateDialog = false;
    public displayAddVehicleModelsDialog = false;
    public displayChangeVehicleModelAvailabilityDialog = false;
    public displayChangeSalesCodesDialog = false;
    public showOnHoldDialog = false;

    public changeStateDialogHeader = '';
    public changeStateDialogMessage = '';
    public changeStateDialogNewSampleState: SampleState = SampleState.CREATED;

    public brandingOptions = [
        { label: 'Standard', value: SampleBranding.STANDARD },
        { label: 'Manufaktur', value: SampleBranding.MANUFAKTUR },
        { label: 'Manufaktur Exclusive', value: SampleBranding.MANUFAKTUR_EXCLUSIVE }
    ];

    constructor(
        private route: ActivatedRoute,
        private sampleService: SampleService,
        private messageService: MessageService,
        private languageService: LanguageService,
        private sampleHelperService: SampleHelperService,
        private sampleProcessLogService: SampleProcessLogService,
        private vehicleModelService: VehicleModelService,
        private marketAvailabilityService: MarketAvailabilityService,
        private confirmationService: ConfirmationService,
        private authService: AuthService,
    ) {
    }

    /**
     * Initializes the component.
     */
    public ngOnInit(): void {
        this.loadSample();
        this.loadSampleVehicleModels()
    }

    /**
     * Returns the language name for the given language ID.
     *
     * @param languageId The ID of the language.
     * @return The name of the language.
     */
    public getLanguageName(languageId: number): string {
        const language = this.languages.find((l: Language) => l.id === languageId);
        return language?.name || 'Unknown';
    }

    /**
     * Opens the edit dialog for the sample.
     */
    public openEditDialog(): void {
        this.editableSample = new SampleWriteDTO(this.sample ?? {});
        this.editableSample.manufacturerId = this.sample?.manufacturer?.id;
        this.displayUpdateDialog = true;
    }

    /**
     * Opens the dialog to add vehicle models to the sample.
     */
    public openAddVehicleModelsDialog(): void {
        this.displayAddVehicleModelsDialog = true;
    }

    /**
     * Opens the dialog to change the sample vehicle model availability.
     */
    public openChangeVehicleModelAvailabilityDialog(vehicleModel: VehicleModel): void {
        this.selectedVehicleModel = vehicleModel;
        this.displayChangeVehicleModelAvailabilityDialog = true;
    }

    public openChangeSalesCodesDialog(vehicleModel: VehicleModel): void {
        this.selectedVehicleModel = vehicleModel;
        this.displayChangeSalesCodesDialog = true;
    }

    public reloadSampleDetails(): void {
        this.loadSample();
        this.loadSampleVehicleModels();
    }

    public publishSample(): void {
        if (!this.sample) {
            return;
        }
        this.changeStateDialogHeader = 'Publish Sample';
        this.changeStateDialogMessage =
            'Are you sure you want to publish this sample? This will send an email to the producer and the R&D department.';
        this.changeStateDialogNewSampleState = SampleState.PUBLISHED;
        this.displayChangeStateDialog = true;
    }

    public requestApproval(): void {
        if (!this.sample) {
            return;
        }
        this.changeStateDialogHeader = 'Request Approval';
        this.changeStateDialogMessage =
            'Are you sure you want to request approval for this sample? This will send an email to the sample reviewer.';
        this.changeStateDialogNewSampleState = SampleState.APPROVAL_REQUESTED;
        this.displayChangeStateDialog = true;
    }

    public requestChangesByReviewer(): void {
        if (!this.sample) {
            return;
        }
        this.changeStateDialogHeader = 'Request Changes';
        this.changeStateDialogMessage =
            'Are you sure you want to request changes for this sample? This will send an email to the producer';
        this.changeStateDialogNewSampleState = SampleState.CHANGE_REQUESTED;
        this.displayChangeStateDialog = true;
    }

    public approveSampleByReviewer(): void {
        if (!this.sample) {
            return;
        }
        this.changeStateDialogHeader = 'Approve Sample';
        this.changeStateDialogMessage =
            'Are you sure you want to approve this sample? This will send an email to the process owner.';
        this.changeStateDialogNewSampleState = SampleState.APPROVED_BY_REVIEWER;
        this.displayChangeStateDialog = true;
    }

    public approveSampleByProcessOwner(): void {
        if (!this.sample) {
            return;
        }
        this.changeStateDialogHeader = 'Set Active';
        this.changeStateDialogMessage = 'Are you sure you want to set this sample active?';
        this.changeStateDialogNewSampleState = SampleState.ACTIVE;
        this.displayChangeStateDialog = true;
    }

    public setInProduction(): void {
        if (!this.sample) {
            return;
        }
        this.changeStateDialogHeader = 'Set In Production';
        this.changeStateDialogMessage = 'Are you sure you want to set this sample to in production?';
        this.changeStateDialogNewSampleState = SampleState.IN_PRODUCTION;
        this.displayChangeStateDialog = true;
    }

    public requestMasterSample(): void {

        this.confirmationService.confirm({
            header: 'Request Master Sample',
            message: 'This will place an order for a physical Master Sample and comes with additional costs. For details about the exact costs, please reach out to RD.\n' +
                'Do you want to place the order?',
            accept: () => {
                if (!this.sample) {
                    return;
                }
                this.sampleService.requestMasterSample(this.sample.id).then(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Master Sample Requested',
                        detail: 'Master sample requested successfully',
                        life: 3000,
                    });
                    this.loadSample();
                }).catch((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000,
                    });
                });
            },
        });
    }

    public deleteSample(): void {
        if (!this.sample) {
            return;
        }
        this.sampleHelperService.deleteSample(this.sample, () => {
            window.history.back();
        });
    }

    public showPublishSampleButton(): boolean {
        if (!this.sample) {
            return false;
        }
        return this.sampleHelperService.showPublishSampleButton(this.sample);
    }

    public showRequestApprovalButton(): boolean {
        if (!this.sample) {
            return false;
        }
        return this.sampleHelperService.showRequestApprovalButton(this.sample);
    }

    public showApproveSampleByReviewerButton(): boolean {
        if (!this.sample) {
            return false;
        }
        return this.sampleHelperService.showApproveSampleByReviewerButton(this.sample);
    }

    public showRequestChangesButton(): boolean {
        if (!this.sample) {
            return false;
        }
        return this.sampleHelperService.showRequestChangesButton(this.sample);
    }

    public showApproveSampleByProcessOwnerButton(): boolean {
        if (!this.sample) {
            return false;
        }
        return this.sampleHelperService.showApproveSampleByProcessOwnerButton(this.sample);
    }

    public showSetInProductionButton(): boolean {
        if (!this.sample) {
            return false;
        }
        return this.sampleHelperService.showSetInProductionButton(this.sample);
    }

    public showSetActiveByProducerButton(): boolean {
        if (!this.sample) {
            return false;
        }
        return this.sampleHelperService.showSetActiveByProducerButton(this.sample);
    }

    /**
     * A process owner also passes the producer role check (role hierarchy), so this is used
     * to hide the producer-only "Set active" button from process owners, who already see it
     * via their own button with the wider APPROVED_BY_REVIEWER/IN_PRODUCTION condition.
     */
    public isProcessOwner(): boolean {
        return this.authService.hasRole(Role.SAMPLE_STUDIO_PROCESS_OWNER);
    }

    public canSeeVehicleModels(): boolean {
        return this.authService.canSeeVehicleModels();
    }

    public showRequestMasterSampleButton(): boolean {
        if (!this.sample) {
            return false;
        }
        return this.sampleHelperService.showRequestMasterSampleButton(this.sample);
    }

    public resendLatestMail(): void {
        this.confirmationService.confirm({
            header: 'Resend Mail',
            message: 'Are you sure you want to resend the latest mail for this sample?',
            accept: () => {
                if (!this.sample) {
                    return;
                }
                this.sampleProcessLogService.resendLatestMail(this.sample.id).then(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Mail Resent',
                        detail: 'Mail resent successfully',
                        life: 3000,
                    });
                }).catch((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000,
                    });
                });
            },
        });
    }

    public resendMail(processLogId: number): void {
        this.confirmationService.confirm({
            header: 'Resend Mail',
            message: 'Are you sure you want to resend the mail for this process step?',
            accept: () => {
                if (!this.sample) {
                    return;
                }
                this.sampleProcessLogService.resendMail(processLogId).then(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Mail Resent',
                        detail: 'Mail resent successfully',
                        life: 3000,
                    });
                }).catch((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000,
                    });
                });
            },
        });
    }

    private loadSample(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            const idNumber = parseInt(id);
            this.sampleService
                .getSample(idNumber)
                .then((result: Sample) => {
                    this.sample = result;
                    this.loadLanguages();
                    this.loadProcessLogs();
                    this.loadMarketAvailability();
                })
                .catch((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000
                    });
                });
        }
    }

    private loadSampleVehicleModels(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (id && this.canSeeVehicleModels()) {
            const idNumber = parseInt(id);
            this.vehicleModelService.getVehicleModelsBySampleId(idNumber)
                .then((vehicleModels: VehicleModel[]) => {
                    this.vehicleModels = vehicleModels.map(vm => {
                        if (Array.isArray(vm.salesCodes)) {
                            vm.salesCodes = [ ...vm.salesCodes ].sort((a, b) => a.localeCompare(b));
                        }
                        return vm;
                    });
                })
                .catch((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000
                    });
                });
        }
    }

    private loadLanguages(): void {
        if (!this.sample) {
            return;
        }

        if (this.sample.sampleLanguages?.length) {
            const languageIds = this.sample.sampleLanguages.map((sl: SampleLanguage) => sl.languageId);
            this.languageService.getMultipleLanguages(languageIds)
                .then((languages: Language[]) => {
                    this.languages = languages;
                })
                .catch((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000
                    });
                });
        }
    }

    private loadProcessLogs(): void {
        if (!this.sample) {
            return;
        }

        this.sampleProcessLogService.getSampleProcessLogs(this.sample.id)
            .then((processLogs: SampleProcessLog[]) => {
                this.processLogs = processLogs;
            })
            .catch((error: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000
                });
            });
    }

    private loadMarketAvailability(): void {
        if (!this.sample) {
            return;
        }

        this.marketAvailabilityService.getBySample(this.sample.id)
            .then((marketAvailabilities: MarketAvailability[]) => {
                this.marketAvailabilities = marketAvailabilities;
            })
            .catch((error: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000
                });
            });
    }

    public removeVehicleModel(vehicleModel: VehicleModel) {
        this.confirmationService.confirm({
            header: 'Remove Vehicle Model',
            message: 'Are you sure you want to remove this vehicle model from the sample?',
            accept: () => {
                this.vehicleModelService.removeSampleFromVehicleModel(vehicleModel.id, this.sample?.id || 0)
                    .then(() => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Vehicle Model Removed',
                            detail: 'Vehicle model removed from sample successfully',
                            life: 3000,
                        });
                        this.reloadSampleDetails();
                    })
                    .catch((error: any) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: error.message,
                            life: 3000,
                        });
                    });
            },
        });
    }

    public onChangeBranding(newBranding: SampleBranding, vehicleModel: VehicleModel) {
        const previousBranding = vehicleModel.branding;

        this.vehicleModelService.changeSampleBranding(vehicleModel.id, this.sample?.id || 0, newBranding)
            .then(() => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Vehicle model branding updated successfully.',
                    life: 3000
                });
            })
            .catch((error: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000
                });

                // Revert to previous value
                vehicleModel.branding = previousBranding;
            });
    }

    public toggleOnHold() {
        this.showOnHoldDialog = true;
    }

    public onVehicleModelSearch($event: Event, vmTable: Table): void {
        const input = $event.target as HTMLInputElement;
        const value = input.value;
        vmTable.filterGlobal(value, 'contains');
    }

    protected readonly SampleState = SampleState;

}
